import { FC, useEffect } from 'react'
import {
    EditOutlined, Logout, ChevronRight,
    AccountBalanceWalletOutlined, ReceiptLongOutlined, PersonOutline,
    SportsEsportsOutlined, MonetizationOnOutlined
} from '@mui/icons-material'
import { Avatar, Box, Button, Chip, Divider, IconButton, Paper, Typography } from '@mui/material'
import { Connect, mapDispatchToProps, mapStateToProps } from '../../Redux'
import { UserProps } from '../../types/user'
import { getFormattedPhone, numberWithCommas } from '../../helpers'
import { getCurrency } from '../../helpers/countries'
import { useNavigate } from 'react-router-dom'
import eventBus from '../../configs/eventsBus'
import avatar from '../../assets/images/avatar.jpg'
import Show from '../common/show'

interface Props {
    User?: UserProps
    logOut?: () => void
    instantPlay?: boolean
}

const Profile: FC<Props> = ({
    User,
    instantPlay,
    logOut
}) => {
    const navigate = useNavigate()
    const currency = getCurrency(User?.country || 'Kenya')

    useEffect(() => {
        eventBus.dispatch('routes', { action: 'getUserData' })
    }, [])

    const stats = [
        {
            label: 'Token Balance',
            value: `${currency} ${numberWithCommas(instantPlay ? 0 : User?.balance || 0)}`,
            icon: <AccountBalanceWalletOutlined />,
            action: !instantPlay ? { text: 'Top Up', onPress: () => navigate('/topup') } : undefined
        },
        { label: 'Withdrawable Earning', value: `${currency} ${numberWithCommas(User?.earning || 0)}`, icon: <MonetizationOnOutlined /> },
        { label: 'Games Played', value: numberWithCommas(User?.totalGamesPlayed || 0), icon: <SportsEsportsOutlined /> },
        { label: 'Total Revenue', value: `${currency} ${numberWithCommas(User?.totalEarning || 0)}`, icon: <MonetizationOnOutlined /> },
    ]

    const links = [
        { label: 'Deposits History', icon: <AccountBalanceWalletOutlined />, onPress: () => navigate('/deposits') },
        { label: 'Check Deposit', icon: <ReceiptLongOutlined />, onPress: () => navigate('/check_deposit') },
        { label: 'Edit Profile', icon: <PersonOutline />, onPress: () => navigate('/edit_profile') },
    ]

    const accountInfo = [
        { label: 'Member Since', value: User?.dateJoined ? new Date(User.dateJoined).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-' },
        { label: 'Country', value: User?.country || '-' },
        { label: 'Withdraw To', value: User?.withdrawTo === 'Till' ? `Till · ${User?.tillNumber || '-'}` : getFormattedPhone(String(User?.Phone || '')) },
    ]

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#111' }}>Profile</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton onClick={() => navigate('/edit_profile')} sx={{ bgcolor: 'rgba(0,107,70,0.08)', color: 'primary.main' }}>
                        <EditOutlined fontSize='small' />
                    </IconButton>
                    <IconButton onClick={logOut} sx={{ bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                        <Logout fontSize='small' />
                    </IconButton>
                </Box>
            </Box>

            {/* Hero */}
            <Paper
                elevation={0}
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    p: { xs: 2.5, sm: 3.5 },
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    background: 'linear-gradient(135deg, #0B6B47 0%, #063D26 100%)',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                }}
            >
                {/* Purely decorative - breaks up the flat gradient a little */}
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

                <Avatar
                    src={User?.Image || avatar}
                    sx={{
                        width: 88,
                        height: 88,
                        border: '3px solid rgba(255,255,255,0.35)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                        zIndex: 1
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0, zIndex: 1 }}>
                    {/* Typography's own default color (text.primary) doesn't inherit the
                        gradient's white - it must be set explicitly here, or it renders as
                        near-invisible dark-on-dark-green. */}
                    <Typography noWrap sx={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.25 }}>
                        {User?.Username}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, mt: 0.3 }}>
                        {getFormattedPhone(String(User?.Phone || ''))}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                            size='small'
                            label={User?.role === 'Super Admin' ? 'Super Admin' : User?.role === 'admin' ? 'Admin' : 'Owner'}
                            sx={{
                                bgcolor: User?.role === 'Super Admin' || User?.role === 'admin' ? '#F5C518' : 'rgba(255,255,255,0.16)',
                                color: User?.role === 'Super Admin' || User?.role === 'admin' ? '#1A1A1A' : '#fff',
                                border: User?.role === 'Super Admin' || User?.role === 'admin' ? 'none' : '1px solid rgba(255,255,255,0.3)',
                                fontWeight: 700,
                                height: 24
                            }}
                        />
                        <Show>
                            <Show.When isTrue={Boolean(instantPlay)}>
                                <Chip size='small' label='Instant Play' sx={{ bgcolor: 'rgba(255,152,0,0.22)', color: '#FFCC80', fontWeight: 700, height: 24 }} />
                            </Show.When>
                        </Show>
                    </Box>
                </Box>
            </Paper>

            {/* Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                {stats.map(({ label, value, icon, action }) => (
                    <Paper key={label} elevation={0} sx={{ borderRadius: 3, p: 2.2, bgcolor: '#F5F5F5' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', mb: 1 }}>
                            {icon}
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.3 }}>{label}</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#111' }}>{value}</Typography>
                        {action && (
                            <Button
                                size='small'
                                onClick={action.onPress}
                                sx={{ textTransform: 'none', mt: 1, bgcolor: '#000', color: '#fff', borderRadius: 5, px: 2, '&:hover': { bgcolor: '#222' } }}
                            >{action.text}</Button>
                        )}
                    </Paper>
                ))}
            </Box>

            {/* Account info */}
            <Paper elevation={0} sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, px: 2.5, pt: 2, pb: 1 }}>
                    Account Info
                </Typography>
                {accountInfo.map(({ label, value }, i) => (
                    <Box key={label}>
                        {i > 0 && <Divider />}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.6 }}>
                            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{label}</Typography>
                            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: '#111', maxWidth: '60%' }}>{value}</Typography>
                        </Box>
                    </Box>
                ))}
            </Paper>

            {/* Quick links */}
            <Paper elevation={0} sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                {links.map(({ label, icon, onPress }, i) => (
                    <Box key={label}>
                        {i > 0 && <Divider />}
                        <Box
                            onClick={onPress}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.8, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}
                        >
                            <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                            <Typography sx={{ flex: 1, fontSize: 14.5, fontWeight: 600, color: '#111' }}>{label}</Typography>
                            <ChevronRight sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </Box>
                    </Box>
                ))}
            </Paper>
        </Box>
    )
}

export default Connect(mapStateToProps, mapDispatchToProps)(Profile)
